/** * null or undefined ""으로 리턴
 * * param: val: unknown
 * * return string
 */
export const getString = (val: unknown): string => {
  if (val == null) {
    return "";
  }
  return String(val);
};

/** * yyyyMMdd yyyy/MM/dd로 리턴
 * * param: val: yyyyMMdd
 * * return string
 */
export const formatToyyyyMMdd = (val: string) =>
  val?.length === 8
    ? `${val.slice(0, 4)}/${val.slice(4, 6)}/${val.slice(6)}`
    : val;

/**
 * 목적: 메타 컨트롤(SnChipInput/SnEditor/SnAudioRecorder)이 문자열로 바꿔둔 jsonb 컬럼 값을
 * 저장 payload용 배열로 되돌린다.
 *
 * 호출 예:
 * const jsParticipant = parseJsonValue(row.js_participant);
 *
 * 주요 기능:
 * 배열 / { type: "jsonb"|"json", value: string } 래퍼 / JSON 문자열 / 객체를 모두 배열로 정규화한다.
 * jsonb 배열 컬럼(js_participant, js_tag, js_attach 등)을 쓰는 어떤 화면에서도 재사용한다.
 *
 * @param value jsonb 컬럼 값, 객체, 배열 또는 JSON 문자열
 * @returns 저장 payload에 넣을 JSON 배열. 빈 값 또는 파싱 실패 시 빈 배열을 반환한다.
 */
export const parseJsonValue = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseJsonValue(item));
  }
  if (
    typeof value === "object" &&
    typeof value.value === "string" &&
    (value.type === "jsonb" || value.type === "json")
  ) {
    return parseJsonValue(value.value);
  }
  if (typeof value === "object") return [value];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parseJsonValue(parsed);
    } catch (error) {
      return [];
    }
  }
  return [];
};

/**
 * 목적: BE Map 타입인 jsonb 객체 컬럼(js_minutes 등) 값을 객체 또는 undefined로 정규화한다.
 *
 * 호출 예:
 * const jsMinutes = parseJsonObject(row.js_minutes);
 *
 * 주요 기능:
 * 값이 배열로 남아 있어도 첫 객체만 사용하고, 빈 값은 payload에서 제외할 수 있게 undefined를 반환한다.
 *
 * @param value jsonb 객체, 객체 배열 또는 JSON 문자열
 * @returns Map 역직렬화가 가능한 객체. 빈 값이면 undefined를 반환한다.
 */
export const parseJsonObject = (value: any): any | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value[0] || undefined;
  if (
    typeof value === "object" &&
    typeof value.value === "string" &&
    (value.type === "jsonb" || value.type === "json")
  ) {
    return parseJsonObject(value.value);
  }
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parseJsonObject(parsed);
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
};
